import * as THREE from 'three'
import type { CompiledWorldChunk } from '../../chunk-schema/src/index.ts'
import { isContinuousRoadSampleSegment } from './chunk-sampling.ts'

export type RoadDebugPage = 0 | 1 | 2

export type RoadDebugStats = {
  splineSegments: number
  nodes: number
  roadTriangles: number
  junctionTriangles: number
}

export type RoadDebugSurface = {
  readonly id: string
  readonly kind: 'road' | 'junction'
  readonly geometry: THREE.BufferGeometry
  readonly triangleCount: number
}

const splineMaterial = new THREE.LineBasicMaterial({
  vertexColors: true,
  transparent: true,
  opacity: 0.96,
  depthTest: false,
  depthWrite: false,
  fog: false,
  toneMapped: false,
})

const nodeMaterial = new THREE.PointsMaterial({
  size: 5,
  sizeAttenuation: false,
  vertexColors: true,
  transparent: true,
  opacity: 1,
  depthTest: false,
  depthWrite: false,
  fog: false,
  toneMapped: false,
})

const roadWireframeMaterial = new THREE.MeshBasicMaterial({
  color: '#ffe14d',
  wireframe: true,
  transparent: true,
  opacity: 0.94,
  depthTest: false,
  depthWrite: false,
  fog: false,
  toneMapped: false,
})

const junctionWireframeMaterial = new THREE.MeshBasicMaterial({
  color: '#ff4dd2',
  wireframe: true,
  transparent: true,
  opacity: 0.94,
  depthTest: false,
  depthWrite: false,
  fog: false,
  toneMapped: false,
})

const splineColours = {
  highway: new THREE.Color('#ff4d5f'),
  major: new THREE.Color('#ffad33'),
  street: new THREE.Color('#00e5ff'),
  service: new THREE.Color('#ad64ff'),
} as const

/**
 * Chunk-local, runtime-only inspection geometry. It deliberately consumes the
 * same road samples used by vehicle support and shares the exact rendered
 * asphalt buffers for wireframe mode.
 */
export class ChunkRoadDebugOverlay {
  readonly root = new THREE.Group()

  private readonly splineRoot = new THREE.Group()
  private readonly wireframeRoot = new THREE.Group()
  private readonly ownedGeometries: THREE.BufferGeometry[] = []
  private readonly surfaceIds = new Set<string>()
  private readonly mutableStats: RoadDebugStats = {
    splineSegments: 0,
    nodes: 0,
    roadTriangles: 0,
    junctionTriangles: 0,
  }

  constructor(
    data: CompiledWorldChunk,
    surfaces: Iterable<RoadDebugSurface>,
  ) {
    this.root.name = 'ROAD_DEBUG'
    this.splineRoot.name = 'ROAD_DEBUG_SPLINES'
    this.wireframeRoot.name = 'ROAD_DEBUG_MESH_WIREFRAME'
    this.root.add(this.splineRoot, this.wireframeRoot)
    this.buildSplinePage(data)
    for (const surface of surfaces) this.addSurface(surface)
    this.setPage(0)
  }

  get stats(): Readonly<RoadDebugStats> {
    return this.mutableStats
  }

  setPage(page: RoadDebugPage): void {
    this.root.visible = page !== 0
    this.splineRoot.visible = page === 1
    this.wireframeRoot.visible = page === 2
  }

  addSurface(surface: RoadDebugSurface): void {
    if (this.surfaceIds.has(surface.id)) return
    this.surfaceIds.add(surface.id)
    const wireframe = new THREE.Mesh(
      surface.geometry,
      surface.kind === 'junction' ? junctionWireframeMaterial : roadWireframeMaterial,
    )
    wireframe.name = `ROAD_DEBUG_WIREFRAME:${surface.id}`
    wireframe.frustumCulled = false
    wireframe.renderOrder = 910
    this.wireframeRoot.add(wireframe)
    if (surface.kind === 'junction') this.mutableStats.junctionTriangles += surface.triangleCount
    else this.mutableStats.roadTriangles += surface.triangleCount
  }

  dispose(): void {
    this.root.removeFromParent()
    this.root.clear()
    this.surfaceIds.clear()
    for (const geometry of this.ownedGeometries) geometry.dispose()
    this.ownedGeometries.length = 0
  }

  private buildSplinePage(data: CompiledWorldChunk): void {
    const roads = data.roads
    const linePositions: number[] = []
    const lineColours: number[] = []
    for (let sample = 1; sample < roads.widths.length; sample += 1) {
      if (!isContinuousRoadSampleSegment(roads, sample)) continue
      const previous = (sample - 1) * 3
      const current = sample * 3
      const colour = splineColour(Math.max(roads.widths[sample - 1], roads.widths[sample]))
      linePositions.push(
        roads.positions[previous], roads.positions[previous + 1] + 0.16, roads.positions[previous + 2],
        roads.positions[current], roads.positions[current + 1] + 0.16, roads.positions[current + 2],
      )
      lineColours.push(colour.r, colour.g, colour.b, colour.r, colour.g, colour.b)
      this.mutableStats.splineSegments += 1
    }
    if (linePositions.length > 0) {
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3))
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(lineColours, 3))
      this.ownedGeometries.push(geometry)
      const lines = new THREE.LineSegments(geometry, splineMaterial)
      lines.name = 'ROAD_DEBUG_AUTHORITY_SPLINES'
      lines.frustumCulled = false
      lines.renderOrder = 900
      this.splineRoot.add(lines)
    }

    const graph = data.roadGraph
    const degrees = new Uint16Array(graph.nodeIds.length)
    for (let edge = 0; edge < graph.edgeNodeIndices.length; edge += 2) {
      degrees[graph.edgeNodeIndices[edge]] += 1
      degrees[graph.edgeNodeIndices[edge + 1]] += 1
    }
    if (graph.nodeIds.length === 0) return
    const nodePositions: number[] = []
    const nodeColours: number[] = []
    for (let node = 0; node < graph.nodeIds.length; node += 1) {
      const offset = node * 3
      nodePositions.push(
        graph.nodePositions[offset],
        graph.nodePositions[offset + 1] + 0.28,
        graph.nodePositions[offset + 2],
      )
      const colour = nodeColour(graph.nodeIds[node], degrees[node])
      nodeColours.push(colour.r, colour.g, colour.b)
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(nodePositions, 3))
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(nodeColours, 3))
    this.ownedGeometries.push(geometry)
    const points = new THREE.Points(geometry, nodeMaterial)
    points.name = 'ROAD_DEBUG_GRAPH_NODES'
    points.frustumCulled = false
    points.renderOrder = 901
    this.splineRoot.add(points)
    this.mutableStats.nodes = graph.nodeIds.length
  }
}

function splineColour(widthMeters: number): THREE.Color {
  if (widthMeters >= 14) return splineColours.highway
  if (widthMeters >= 9) return splineColours.major
  if (widthMeters >= 5.8) return splineColours.street
  return splineColours.service
}

function nodeColour(id: string, degree: number): THREE.Color {
  if (id.startsWith('boundary:')) return new THREE.Color('#b57cff')
  if (degree >= 3) return new THREE.Color('#ff4d5f')
  if (degree === 2) return new THREE.Color('#ffd24d')
  return new THREE.Color('#f4f7ef')
}
